import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Platform } from '../Icons.jsx';
import { SlideSwiper } from './SlideSwiper.jsx';
import { pickPlatformText, joinHashtags } from './platformText.js';

function getInitials(name) {
  if (!name) return 'YB';
  return String(name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() || '')
    .join('') || 'YB';
}

function makeText(post, platform, fallback) {
  return pickPlatformText(post, platform) || fallback;
}

const SkeletonLines = () => (
  <div className="preview-skeleton">
    <div className="preview-skeleton-line" style={{ width: '90%' }} />
    <div className="preview-skeleton-line" style={{ width: '76%' }} />
    <div className="preview-skeleton-line" style={{ width: '60%' }} />
  </div>
);

// =================== INSTAGRAM ===================
export const InstagramCard = ({ post, brand, accountMeta, slideIndex, onSlideChange, onOpenImage }) => {
  const { t } = useTranslation();
  const brandLabel = brand || t('platformCards.yourBrand');
  const isCarousel = post?.format === 'carousel' && Array.isArray(post.slides) && post.slides.length > 0;
  const image = isCarousel ? null : primaryMediaImage(post);
  const text = makeText(post, 'instagram', t('platformCards.generationInProgress'));
  const isGenerating = post?.status === 'generating';
  const hashtags = joinHashtags(post?.hashtags);
  return (
    <>
      <div className="ig-head">
        <div className="ig-avatar" />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 600 }}>{brandLabel}</div>
          <div style={{ fontSize: 9, color: '#9CA3AF' }}>{accountMeta || t('platformCards.justNow')}</div>
        </div>
        <span style={{ fontSize: 14 }}>⋯</span>
      </div>
      {isCarousel ? (
        <SlideSwiper slides={post.slides} brand={brandLabel} activeIndex={slideIndex} onActiveChange={onSlideChange} onOpenImage={onOpenImage} />
      ) : image ? (
        <button type="button" className="ig-flat ig-flat-button has-image" onClick={() => onOpenImage?.(image)}>
          <img className="ig-flat-image" src={image.src} alt={image.alt} loading="lazy" />
        </button>
      ) : (
        <div className="ig-flat">
          <div className="ig-flat-art">
            <div className="ig-flat-emoji">✨</div>
          </div>
        </div>
      )}
      <div className="ig-actions">
        <Icon name="heart" size={20} />
        <Icon name="msg" size={20} />
        <Icon name="send" size={20} />
        <span style={{ flex: 1 }} />
        <Icon name="bookmark" size={20} />
      </div>
      <div className="ig-caption">
        <div className="ig-caption-author">{brandLabel}</div>
        {isGenerating ? <SkeletonLines /> : (
          <div className="ig-caption-text">
            {text}
            {hashtags && <div className="ig-caption-hashtags">{hashtags}</div>}
          </div>
        )}
      </div>
    </>
  );
};

// =================== LINKEDIN ===================
export const LinkedInCard = ({ post, brand }) => {
  const { t } = useTranslation();
  const brandLabel = brand || t('platformCards.yourBrand');
  const text = makeText(post, 'linkedin', t('platformCards.generationInProgress'));
  const isGenerating = post?.status === 'generating';
  const image = primaryMediaImage(post);
  return (
    <div className="li-feed">
      <div className="li-author">
        <div className="li-avatar">{getInitials(brandLabel)}</div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="li-name">{brandLabel}</div>
          <div className="li-meta">{t('platformCards.justNowPublic')}</div>
        </div>
        <button className="li-follow">{t('platformCards.follow')}</button>
      </div>
      <div className="li-body">
        {isGenerating ? <SkeletonLines /> : (
          <div className="li-text">{text}</div>
        )}
      </div>
      {!isGenerating && image && <img className="li-media-image" src={image.src} alt={image.alt} loading="lazy" />}
      <div className="li-stats">
        <span>👍 ❤️ 💡 142</span>
        <span style={{ flex: 1 }} />
        <span>{t('platformCards.comments23')}</span>
      </div>
      <div className="li-feed-actions">
        <span><Icon name="heart" size={14} /> {t('platformCards.like')}</span>
        <span><Icon name="msg" size={14} /> {t('platformCards.comment')}</span>
        <span><Icon name="send" size={14} /> {t('platformCards.repost')}</span>
        <span><Icon name="send" size={14} /> {t('platformCards.send')}</span>
      </div>
    </div>
  );
};

// =================== X / TWITTER ===================
export const XCard = ({ post, brand }) => {
  const { t } = useTranslation();
  const brandLabel = brand || t('platformCards.yourBrand');
  const text = makeText(post, 'x', t('platformCards.generationInProgress'));
  const isGenerating = post?.status === 'generating';
  return (
    <div className="x-feed">
      <div className="x-row">
        <div className="x-avatar">{getInitials(brandLabel)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="x-author">
            <span className="x-name">{brandLabel}</span>
            <span className="x-handle">@{String(brandLabel).toLowerCase().replace(/\s+/g, '')}</span>
            <span className="x-dot">·</span>
            <span className="x-time">{t('platformCards.now')}</span>
          </div>
          {isGenerating ? <SkeletonLines /> : (
            <div className="x-text">{text}</div>
          )}
          <div className="x-actions">
            <span><Icon name="msg" size={13} /> 12</span>
            <span><Icon name="send" size={13} /> 4</span>
            <span><Icon name="heart" size={13} /> 84</span>
            <span><Icon name="bookmark" size={13} /></span>
          </div>
        </div>
      </div>
    </div>
  );
};

// =================== TIKTOK ===================
export const TikTokCard = ({ post, brand }) => {
  const { t } = useTranslation();
  const brandLabel = brand || t('platformCards.yourBrand');
  const text = makeText(post, 'tiktok', t('platformCards.generationInProgress'));
  const isGenerating = post?.status === 'generating';
  return (
    <div className="tt-feed">
      <div className="tt-stage">
        <div className="tt-stage-art">▶</div>
        <div className="tt-side">
          <div className="tt-side-avatar">{getInitials(brandLabel)}</div>
          <div className="tt-side-stat"><Icon name="heart" size={18} /><span>2.1k</span></div>
          <div className="tt-side-stat"><Icon name="msg" size={18} /><span>342</span></div>
          <div className="tt-side-stat"><Icon name="send" size={18} /><span>{t('platformCards.share')}</span></div>
        </div>
        <div className="tt-bottom">
          <div className="tt-author">@{String(brandLabel).toLowerCase().replace(/\s+/g, '')}</div>
          {isGenerating ? <SkeletonLines /> : (
            <div className="tt-caption">{text}</div>
          )}
        </div>
      </div>
    </div>
  );
};

// =================== TELEGRAM ===================
export const TelegramCard = ({ post, brand, accountMeta, onOpenImage }) => {
  const { t } = useTranslation();
  const brandLabel = brand || t('platformCards.yourBrand');
  const text = makeText(post, 'telegram', t('platformCards.generationInProgress'));
  const isGenerating = post?.status === 'generating';
  const hasCarousel = post?.format === 'carousel' && Array.isArray(post.slides) && post.slides.length > 0;
  const image = hasCarousel ? null : primaryMediaImage(post);
  const visibleSlides = hasCarousel ? post.slides.slice(0, 4) : [];
  const hiddenSlideCount = hasCarousel ? Math.max(0, post.slides.length - visibleSlides.length) : 0;
  return (
    <div className="tg-feed">
      <div className="tg-appbar">
        <Icon name="chev_l" size={17} />
        <div className="tg-avatar">{getInitials(brandLabel)}</div>
        <div className="tg-channel-copy">
          <div className="tg-channel-name">{brandLabel}</div>
          <div className="tg-channel-meta">{accountMeta || t('platformCards.platformChannel', { platform: 'Telegram' })}</div>
        </div>
        <Icon name="more" size={16} />
      </div>
      <div className="tg-chat">
        <div className="tg-date-pill">{t('platformCards.today')}</div>
        <div className="tg-message-card">
          {hasCarousel && (
            <div className={'tg-album tg-album-count-' + visibleSlides.length}>
              {visibleSlides.map((slide, index) => (
                <button
                  key={slide.id || index}
                  type="button"
                  className={'tg-album-tile ' + (slide.imageUrl ? 'has-image' : '')}
                  style={slide.imageUrl ? { backgroundImage: `url(${slide.imageUrl})` } : undefined}
                  onClick={slide.imageUrl ? () => onOpenImage?.(imagePayload(slide, index)) : undefined}
                  disabled={!slide.imageUrl}
                >
                  {!slide.imageUrl && (
                    <>
                      <div className="tg-album-index">{index + 1}</div>
                      <div className="tg-album-title">{slide.headline || slide.label || t('platformCards.slideNumber', { number: index + 1 })}</div>
                    </>
                  )}
                  {hiddenSlideCount > 0 && index === visibleSlides.length - 1 && (
                    <div className="tg-album-more">+{hiddenSlideCount}</div>
                  )}
                </button>
              ))}
            </div>
          )}
          {!hasCarousel && image && (
            <div className="tg-single-media">
              <button
                type="button"
                className="tg-single-media-button"
                onClick={() => onOpenImage?.(image)}
                aria-label={t('platformCards.openPlatformMedia', { platform: 'Telegram' })}
              >
                <img src={image.src} alt={image.alt} loading="lazy" decoding="async" />
              </button>
            </div>
          )}
          <div className="tg-msg">
            {isGenerating ? <SkeletonLines /> : (
              <div className="tg-msg-text">{text}</div>
            )}
            <div className="tg-msg-meta">
              <Icon name="eye" size={11} /> {t('platformCards.viewsAfterPublish')} <span>{t('platformCards.justNow')}</span> <Icon name="check" size={11} />
            </div>
          </div>
        </div>
        <div className="tg-action-strip">
          <span><Icon name="send" size={12} /> {t('platformCards.forward')}</span>
          <span><Icon name="msg" size={12} /> {t('platformCards.comment')}</span>
        </div>
      </div>
    </div>
  );
};

function imagePayload(slide, index) {
  return {
    src: slide.imageUrl,
    alt: slide.headline || slide.label || `Slide ${index + 1}`,
    label: slide.headline || slide.label || `Slide ${index + 1}`,
  };
}

function primaryMediaImage(post) {
  const slide = Array.isArray(post?.slides)
    ? post.slides.find(item => item?.imageUrl && !item.imageIsPlaceholder)
    : null;
  if (slide?.imageUrl) return imagePayload(slide, Number(slide.order || 0));

  const asset = Array.isArray(post?.mediaAssets)
    ? post.mediaAssets.find(item => item?.publicUrl || item?.imageUrl || item?.url)
    : null;
  const src = asset?.publicUrl || asset?.imageUrl || asset?.url;
  if (!src) return null;
  return {
    src,
    alt: asset.alt || asset.label || post?.title || 'Generated image',
    label: asset.label || asset.alt || post?.title || 'Generated image',
  };
}

// =================== FACEBOOK ===================
export const FacebookCard = ({ post, brand, onOpenImage }) => {
  const { t } = useTranslation();
  const brandLabel = brand || t('platformCards.yourBrand');
  const text = makeText(post, 'facebook', t('platformCards.generationInProgress'));
  const isGenerating = post?.status === 'generating';
  const media = facebookPreviewMedia(post);
  const visibleMedia = media.slice(0, 4);
  const hiddenMediaCount = Math.max(0, media.length - visibleMedia.length);
  return (
    <div className="fb-feed">
      <div className="fb-author">
        <div className="fb-avatar">{getInitials(brandLabel)}</div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="fb-name">{brandLabel}</div>
          <div className="fb-meta">{t('platformCards.justNowPublic')}</div>
        </div>
      </div>
      {isGenerating ? <SkeletonLines /> : (
        <div className="fb-text">{text}</div>
      )}
      {visibleMedia.length > 0 && (
        <div className={'fb-media-grid fb-media-count-' + visibleMedia.length}>
          {visibleMedia.map((image, index) => (
            <button
              key={image.src + index}
              type="button"
              className="fb-media-tile"
              style={{ backgroundImage: `url(${image.src})` }}
              onClick={() => onOpenImage?.(image)}
              aria-label={t('platformCards.openPlatformMediaNumber', { platform: 'Facebook', number: index + 1 })}
            >
              {hiddenMediaCount > 0 && index === visibleMedia.length - 1 && (
                <span className="fb-media-more">+{hiddenMediaCount}</span>
              )}
            </button>
          ))}
        </div>
      )}
      <div className="fb-stats">
        <span>{t('platformCards.fbStats')}</span>
      </div>
      <div className="fb-actions">
        <span><Icon name="heart" size={14} /> {t('platformCards.like')}</span>
        <span><Icon name="msg" size={14} /> {t('platformCards.comment')}</span>
        <span><Icon name="send" size={14} /> {t('platformCards.share')}</span>
      </div>
    </div>
  );
};

function facebookPreviewMedia(post) {
  const slides = post?.format === 'carousel' && Array.isArray(post.slides) ? post.slides : [];
  const slideImages = slides
    .filter(slide => slide?.imageUrl)
    .map((slide, index) => imagePayload(slide, index));
  if (slideImages.length) return slideImages;

  return (Array.isArray(post?.mediaAssets) ? post.mediaAssets : [])
    .map((asset, index) => {
      const src = asset?.publicUrl || asset?.imageUrl || asset?.url;
      if (!src) return null;
      return {
        src,
        alt: asset.alt || asset.label || `Facebook media ${index + 1}`,
        label: asset.label || asset.alt || `Facebook media ${index + 1}`,
      };
    })
    .filter(Boolean);
}

// =================== THREADS ===================
export const ThreadsCard = ({ post, brand }) => {
  const { t } = useTranslation();
  const brandLabel = brand || t('platformCards.yourBrand');
  const text = makeText(post, 'threads', t('platformCards.generationInProgress'));
  const isGenerating = post?.status === 'generating';
  return (
    <div className="th-feed">
      <div className="th-row">
        <div className="th-avatar-col">
          <div className="th-avatar">{getInitials(brandLabel)}</div>
          <div className="th-thread-line" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="th-author">
            <span className="th-name">{String(brandLabel).toLowerCase().replace(/\s+/g, '')}</span>
            <span className="th-time">{t('platformCards.dotNow')}</span>
          </div>
          {isGenerating ? <SkeletonLines /> : (
            <div className="th-text">{text}</div>
          )}
          <div className="th-actions">
            <span><Icon name="heart" size={14} /></span>
            <span><Icon name="msg" size={14} /></span>
            <span><Icon name="send" size={14} /></span>
            <span><Icon name="bookmark" size={14} /></span>
          </div>
        </div>
      </div>
    </div>
  );
};

const CARD_REGISTRY = {
  instagram: InstagramCard,
  linkedin: LinkedInCard,
  x: XCard,
  twitter: XCard,
  tiktok: TikTokCard,
  telegram: TelegramCard,
  facebook: FacebookCard,
  threads: ThreadsCard,
};

export const PlatformCard = ({ platform, ...props }) => {
  const Component = CARD_REGISTRY[platform] || InstagramCard;
  return <Component {...props} />;
};

export default PlatformCard;
